import { EstadoConexion } from './EstadoConexion.js'
import type { Sesion } from '../datos/sesion.js'
import type { Vista } from './navegacion.js'

/**
 * La barra de navegación, ahora vertical.
 *
 * El scroll horizontal era el síntoma de haber apretado dos ejes en una fila:
 * módulos de la clínica y paciente abierto competían por el mismo espacio, y
 * con seis pestañas, el nombre de la clínica y el indicador de conexión, el
 * botón de salir terminaba cortado fuera de la pantalla. Una columna elimina
 * el problema de raíz: cada entrada tiene su fila y cabe cualquier cantidad.
 */

export function Nav({
  sesion,
  vista,
  onVista,
  onSalir,
  pendientes,
}: {
  sesion: Sesion
  vista: Vista
  onVista: (v: Vista) => void
  onSalir: () => void
  pendientes?: number | undefined
}) {
  const entradas: [Vista, string][] = [
    ['hoy', 'Hoy'],
    ['agenda', 'Agenda'],
    ['consultorios', 'Consultorios'],
    ['pacientes', 'Pacientes'],
    ['dinero', 'Caja'],
    ['reportes', 'Reportes'],
  ]

  return (
    <aside className="flex h-full w-52 shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="px-4 py-4">
        <span className="block font-medium text-teal-700">practiClinic</span>
        <span className="block truncate text-sm text-slate-500">
          {sesion.tenant.nombre}
        </span>
      </div>

      <nav className="flex-1 px-2">
        {entradas.map(([id, etiqueta]) => (
          <button
            key={id}
            type="button"
            onClick={() => onVista(id)}
            className={`flex min-h-10 w-full items-center rounded-lg px-3 text-sm transition ${
              vista === id
                ? 'bg-teal-700 font-medium text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <span className="flex-1 text-left">{etiqueta}</span>
            {id === 'hoy' && pendientes !== undefined && pendientes > 0 && (
              <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-xs font-medium text-white">
                {pendientes}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="border-t border-slate-200 px-2 py-2">
        {/* Configuración es sólo de admin: recepción opera dentro de las reglas, no
            las cambia. */}
        {sesion.rol === 'admin' && (
          <button
            type="button"
            onClick={() => onVista('config')}
            className={`flex min-h-10 w-full items-center rounded-lg px-3 text-sm transition ${
              vista === 'config'
                ? 'bg-teal-700 font-medium text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            Configuración
          </button>
        )}

        <div className="mt-2 flex items-center gap-3 px-3 py-2">
          <EstadoConexion />
          <button
            type="button"
            onClick={onSalir}
            className="text-sm text-slate-500 hover:text-slate-900"
          >
            Salir
          </button>
        </div>
      </div>
    </aside>
  )
}
